import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, ArrowRight, Clock3, RefreshCw, Search, ShieldCheck, UserRound } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { feishuTaskAction } from '../lib/licensePresentation'
import type { FeishuPersonalTask, FeishuPersonalTaskInbox, FeishuTaskInboxCategory, FeishuTaskStage } from '../types'

const categories: Array<{ value: FeishuTaskInboxCategory, label: string }> = [
  { value: 'ALL', label: '全部' },
  { value: 'RECEIVED_UNCLAIMED', label: '待领取' },
  { value: 'CLAIMED_BY_ME', label: '我已领取' },
  { value: 'OVERDUE', label: '已超时' },
  { value: 'BLOCKED_CONFIGURATION', label: '配置阻塞' },
]

const stages: Array<{ value: FeishuTaskStage, label: string }> = [
  { value: 'ALL', label: '全部阶段' },
  { value: 'APPROVAL', label: '审批' },
  { value: 'ISSUANCE', label: '签发' },
  { value: 'DELIVERY_ACTIVATION', label: '交付激活' },
  { value: 'VALIDATION_EXCEPTION', label: '授权检查异常' },
  { value: 'RENEWAL', label: '续期' },
  { value: 'OTHER', label: '其他事项' },
]

export function PersonalTasksPage() {
  const [searchParams] = useSearchParams()
  const initialStage = normalizedStage(searchParams.get('stage'))
  const [inbox, setInbox] = useState<FeishuPersonalTaskInbox>()
  const [category, setCategory] = useState<FeishuTaskInboxCategory>('ALL')
  const [stage, setStage] = useState<FeishuTaskStage>(initialStage)
  const [keywordDraft, setKeywordDraft] = useState(searchParams.get('keyword') || '')
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '')
  const [page, setPage] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const syncInFlight = useRef(false)

  const refreshInbox = useCallback(async (silent = false) => {
    if (syncInFlight.current) return
    syncInFlight.current = true
    if (!silent) setRefreshing(true)
    try {
      const current = await api.feishuTaskInbox({ category, stage, keyword, page, size: 20 })
      setInbox(current); setError('')
    } catch (value) {
      setError(value instanceof Error ? value.message : '个人任务同步失败')
    } finally {
      syncInFlight.current = false
      if (!silent) setRefreshing(false)
    }
  }, [category, keyword, page, stage])

  useEffect(() => { void refreshInbox() }, [refreshInbox])
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshInbox(true)
    }, 15_000)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshInbox(true)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisibility) }
  }, [refreshInbox])

  function search(event: FormEvent) {
    event.preventDefault(); setPage(0); setKeyword(keywordDraft.trim())
  }

  return <div className="personal-task-page">
    <div className="page-lead personal-task-lead"><div><h2>个人任务箱</h2><p>唯一待办入口：只展示明确分配给你或等待你认领的权威任务实例。</p></div><aside className={inbox?.urgentPersonal ? 'is-urgent' : ''}><strong>{inbox?.totalPersonal || 0}</strong><span>我的当前任务</span><small>{inbox?.urgentPersonal ? `${inbox.urgentPersonal} 项已超时或配置阻塞` : '暂无紧急事项'}</small></aside></div>
    <section className="feishu-task-panel feishu-inbox platform-task-inbox">
      <header className="feishu-inbox-heading"><div><h3>责任任务</h3><p>状态每 15 秒自动同步。单笔 License 的业务全景统一进入交付单。</p></div><div className="feishu-sync-state"><span className={refreshing ? 'is-syncing' : ''}><RefreshCw size={13} />{refreshing ? '同步中' : inbox ? `同步于 ${formatTime(inbox.serverTime)}` : '等待同步'}</span><button type="button" onClick={() => void refreshInbox()} disabled={refreshing}><RefreshCw size={14} />立即同步</button></div></header>
      <nav className="feishu-inbox-categories" aria-label="任务分类">{categories.map(item => <button type="button" className={category === item.value ? 'is-active' : ''} onClick={() => { setCategory(item.value); setPage(0) }} key={item.value}><span>{item.label}</span><b>{inbox?.counts[item.value] || 0}</b></button>)}</nav>
      <form className="feishu-inbox-filters" onSubmit={search}><label><span>当前阶段</span><select value={stage} onChange={event => { setStage(event.target.value as FeishuTaskStage); setPage(0) }}>{stages.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label><label className="feishu-inbox-search"><span>任务搜索</span><div><Search size={15} /><input value={keywordDraft} onChange={event => setKeywordDraft(event.target.value)} placeholder="License ID、安装实例或任务号" /><button type="submit">查询</button></div></label></form>
      {error && <div className="feishu-inbox-error"><AlertTriangle size={16} /><span>{error}</span><button type="button" onClick={() => void refreshInbox()}>重试</button></div>}
      <div className="feishu-inbox-list">{inbox?.items.length ? inbox.items.map(task => <TaskRow task={task} key={task.taskId} />) : <div className="feishu-task-empty"><ShieldCheck size={24} /><b>当前筛选下没有待处理任务</b><span>新的责任任务到达后会自动出现在这里。</span></div>}</div>
      {(inbox?.totalPages || 0) > 1 && <footer className="feishu-inbox-pagination"><span>共 {inbox?.totalElements} 条，第 {(inbox?.page || 0) + 1} / {inbox?.totalPages} 页</span><div><button type="button" disabled={page === 0} onClick={() => setPage(value => Math.max(0, value - 1))}>上一页</button><button type="button" disabled={page + 1 >= (inbox?.totalPages || 0)} onClick={() => setPage(value => value + 1)}>下一页</button></div></footer>}
    </section>
    <p className="personal-task-footnote">审批台、签发台和交付模块用于精确办理或批量查询，不再作为待办发现入口。</p>
  </div>
}

function TaskRow({ task }: { task: FeishuPersonalTask }) {
  const urgency = ({ BLOCKED: '阻塞', OVERDUE: '超时', DUE_SOON: '临近', NORMAL: '待办' } as Record<string, string>)[task.urgency] || '待办'
  const action = feishuTaskAction(task)
  return <Link className="feishu-inbox-task" to={`/tasks/${encodeURIComponent(task.taskId)}`}>
    <div className="feishu-inbox-task-main"><div className="feishu-inbox-task-title"><span className={`feishu-task-urgency feishu-task-urgency--${task.urgency.toLowerCase()}`}>{urgency}</span>{task.unread && <i title="未读" />}<b>{action.label}</b></div><p className="feishu-inbox-task-action">{action.detail}</p><div className="feishu-inbox-task-meta">{task.customerName && <span>{task.customerName}</span>}{task.licenseId && <span>License {task.licenseId}</span>}{task.installationId && <span>实例 {task.installationId}</span>}<span>任务 {task.taskId}</span></div>{task.configurationBlocked && <p><AlertTriangle size={13} />{task.blockingReason || '投递配置阻塞，请联系管理员处理'}</p>}</div>
    <dl><div><dt><UserRound size={12} />责任状态</dt><dd>{task.claimedBy ? `已由 ${task.claimedBy} 领取` : task.assignedTo ? `已分配给 ${task.assignedTo}，可直接办理` : '岗位待领取'}</dd></div><div><dt><Clock3 size={12} />截止时间</dt><dd className={task.overdue ? 'is-overdue' : ''}>{task.deadlineAt ? formatDeadline(task.deadlineAt, task.overdue) : '未设置 SLA'}</dd></div><div><dt>提醒 / 动态</dt><dd>{task.notificationStatus} · {task.signalCount} 条阶段动态</dd></div></dl><ArrowRight size={17} />
  </Link>
}

function normalizedStage(value: string | null): FeishuTaskStage {
  return stages.some(item => item.value === value) ? value as FeishuTaskStage : 'ALL'
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(new Date(value))
}

function formatDeadline(value: string, overdue: boolean) {
  const formatted = new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value))
  return `${formatted}${overdue ? '（已超时）' : ''}`
}
